import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Box, Group, Stack, Table, UnstyledButton } from "@mantine/core";

import { IconCircleMinus } from "@tabler/icons-react";

import { MissingServicesTableRow } from "@/billing/types/missing-services";
import AnchorLink from "@/clients/components/AnchorLink";
import ConfirmationModal from "@/components/ConfirmationModal";
import { Loading } from "@/components/Loading";
import SortableTh from "@/components/SortableTh";
import StatusBadge from "@/components/StatusBadge";
import { TextWrapper } from "@/components/TextWrapper";
import PermissionGate from "@/permission/components/PermissionGate";
import { BILLER_MANAGER_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { useDestructiveConfirmAction } from "@/utils/hooks/use-destructive-confirm-action";

import {
  EMPTY_MESSAGE,
  LOAD_ERROR_MESSAGE,
  MISSING_SERVICE_STATUS_DISPLAY,
  MISSING_SERVICES_COLUMNS,
  REMOVE_ICON_SIZE,
  TABLE_HEADER_HEIGHT,
} from "./constants/missing-services";
import MissingServicesTh from "./MissingServicesTh";
import { MissingServicesTableProps } from "./types/missing-services";

export default function MissingServicesTable({
  rows,
  isLoading,
  error,
  onRemove,
  onRefresh,
}: MissingServicesTableProps) {
  const [removeTarget, setRemoveTarget] = useState<MissingServicesTableRow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const handleSort = (sortKey: string | null) => {
    setSearchParams((prev) => {
      if (sortKey) {
        prev.set("sort", sortKey);
      } else {
        prev.delete("sort");
      }
      return prev;
    });
  };
  const { isLoading: isRemoving, handleConfirm } = useDestructiveConfirmAction({
    opened: removeTarget !== null,
    onClose: () => setRemoveTarget(null),
    onRefresh,
    action: async () => {
      if (!removeTarget) return { ok: false };
      const removed = await onRemove(removeTarget.encounterId);
      return removed ? { ok: true } : { ok: false };
    },
    successToast: {
      title: "Encounter removed",
      message: "The encounter was removed from Missing Services.",
    },
    errorToast: {
      title: "Failed to remove encounter",
      message:
        "The encounter could not be removed from Missing Services. Try again or contact support if this continues.",
    },
  });

  if (error) {
    return (
      <TextWrapper size="md" c="red.7" data-testid="missing-services-table-error">
        {LOAD_ERROR_MESSAGE}
      </TextWrapper>
    );
  }

  if (!isLoading && rows.length === 0) {
    return (
      <TextWrapper size="md" c="gray.7" data-testid="missing-services-table-empty">
        {EMPTY_MESSAGE}
      </TextWrapper>
    );
  }

  return (
    <>
      <Box
        data-testid="missing-services-table"
        style={{
          overflow: "hidden",
          borderRadius: "var(--mantine-radius-md)",
          border: "1px solid var(--mantine-color-cream-4)",
        }}
      >
        <Table withRowBorders verticalSpacing="md" borderColor="cream.4" horizontalSpacing="lg">
          <Table.Thead bg="cream.2">
            <Table.Tr h={TABLE_HEADER_HEIGHT}>
              {MISSING_SERVICES_COLUMNS.map((column) =>
                column.sortKey ? (
                  <SortableTh
                    key={column.key}
                    w={column.width}
                    ta={column.align}
                    onSort={handleSort}
                    sortKey={column.sortKey}
                    searchParams={searchParams}
                    data-testid={`missing-services-sort-${column.key}`}
                  >
                    {column.label}
                  </SortableTh>
                ) : (
                  <MissingServicesTh column={column} key={column.key}>
                    {column.label}
                  </MissingServicesTh>
                ),
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr data-testid="missing-services-table-loading">
                <Table.Td colSpan={MISSING_SERVICES_COLUMNS.length}>
                  <Loading height={96} />
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row) => (
                <Table.Tr key={row.encounterId}>
                  <Table.Td>
                    <Stack gap={0}>
                      <AnchorLink
                        fw={600}
                        size="md"
                        c="burgundy.9"
                        to={`/clients/${row.clientId}/encounters/${row.encounterId}`}
                        data-testid={`missing-services-client-name-${row.encounterId}`}
                      >
                        {row.clientName}
                      </AnchorLink>
                      <TextWrapper size="md" c="gray.7">
                        {`MRN ${row.mrn}`}
                      </TextWrapper>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper size="md" c="dark.9">
                      {row.encounterDate}
                    </TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper size="md" c="dark.9">
                      {row.encounterType}
                    </TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper size="md" c="dark.9">
                      {row.provider}
                    </TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge
                      color={MISSING_SERVICE_STATUS_DISPLAY[row.serviceStatus].color}
                      status={MISSING_SERVICE_STATUS_DISPLAY[row.serviceStatus].label}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group justify="flex-end">
                      <PermissionGate
                        allowPatient={false}
                        requiredPermissions={BILLER_MANAGER_PERMISSION_FLAGS}
                      >
                        <UnstyledButton
                          onClick={() => setRemoveTarget(row)}
                          data-testid={`missing-services-remove-${row.encounterId}`}
                        >
                          <Group gap="xs" wrap="nowrap" align="center">
                            <IconCircleMinus
                              aria-hidden
                              size={REMOVE_ICON_SIZE}
                              color="var(--mantine-color-red-7)"
                            />
                            <TextWrapper size="md" c="red.7" variant="bold">
                              Remove
                            </TextWrapper>
                          </Group>
                        </UnstyledButton>
                      </PermissionGate>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>
      <ConfirmationModal
        isDeleting={isRemoving}
        onConfirm={handleConfirm}
        opened={removeTarget !== null}
        confirmButtonText="Yes, Remove"
        onClose={() => setRemoveTarget(null)}
        modalTestId="missing-services-remove-modal"
        title="Remove this encounter from Missing Services?"
        cancelButtonTestId="missing-services-remove-cancel-button"
        confirmButtonTestId="missing-services-remove-confirm-button"
        description={`This will remove ${removeTarget?.clientName ?? "this client"}'s encounter from the Missing Services queue.`}
      />
    </>
  );
}
